import { useState, useCallback, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { BatchTablePreview } from '../components/batch/BatchConfirmDialog'
import { GenNode } from '../types/carbon/models/GenNode'
import { processNodes, sortNodesRecursively } from '../utils/tree'
import { generateNodeId } from '../utils/axisHelpers'

interface VariableMetadata {
  name: string
  description: string
}

export interface UseBatchGenerateProps {
  customer: string
  job: string
}

export const useBatchGenerate = ({ customer, job }: UseBatchGenerateProps) => {
  const [topSyntax, setTopSyntax] = useState('')
  const [topNodes, setTopNodes] = useState<GenNode[]>([])
  const [topAxisError, setTopAxisError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [weight, setWeight] = useState('')
  const [caseFilter, setCaseFilter] = useState('')
  const [prefix, setPrefix] = useState('')
  const [suffix, setSuffix] = useState('')
  const [destinationFolder, setDestinationFolder] = useState('')
  const [destination, setDestination] = useState<'user' | 'exec'>('user')
  const [loading, setLoading] = useState(false)
  const [descriptions, setDescriptions] = useState<Record<string, string>>({})
  const [filterTargetNode, setFilterTargetNode] = useState<GenNode | null>(null)
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const topSyntaxRequestId = useRef(0)

  const generateTableName = useCallback((varName: string) => {
    return `${prefix}${varName}${suffix}`
  }, [prefix, suffix])

  // Convert syntax string to axis nodes via Carbon API
  const getNodesFromSyntax = useCallback(async (syntax: string): Promise<GenNode[]> => {
    const url = `${API_BASE_URL}/tables/${customer}/${job}/convert/tonodes`
    const response = await axios.post<GenNode[]>(url, { syntax })
    const nodes = response.data || []

    const nodesWithID = processNodes(nodes, node => {
      node.id = generateNodeId()
      return node
    })

    const tabAxisNode = nodesWithID.find(node => node.type === 'TabAxis')
    const children = tabAxisNode?.children || []
    return sortNodesRecursively(children)
  }, [customer, job])

  // Convert axis nodes to syntax string via Carbon API
  const refreshTopSyntax = useCallback(async (nodes: GenNode[]) => {
    const requestId = topSyntaxRequestId.current + 1
    topSyntaxRequestId.current = requestId

    if (!nodes || nodes.length === 0) {
      setTopSyntax('')
      setTopAxisError(null)
      return
    }

    if (!customer || !job) {
      setTopSyntax('')
      setTopAxisError(null)
      return
    }

    try {
      const url = `${API_BASE_URL}/tables/${customer}/${job}/convert/fromnodes`
      const response = await axios.post<string>(url, nodes)
      if (topSyntaxRequestId.current === requestId) {
        setTopSyntax(response.data || '')
        setTopAxisError(null)
      }
    } catch (error) {
      console.error('Failed to convert top axis nodes to syntax:', error)
      if (topSyntaxRequestId.current === requestId) {
        setTopSyntax('')
        setTopAxisError('Failed to convert top axis to syntax')
      }
    }
  }, [customer, job])

  // Called when nodes are modified (reorder, remove, etc.)
  const handleTopAxisChange = useCallback((nodes: GenNode[]) => {
    setTopNodes(nodes)
    setTopAxisError(null)
    refreshTopSyntax(nodes)
  }, [refreshTopSyntax])

  // Called when user edits syntax text and clicks Apply
  const handleTopSyntaxApply = useCallback(async (syntax: string) => {
    if (!customer || !job) {
      setTopAxisError('Customer or job is missing')
      throw new Error('Customer or job is missing')
    }

    try {
      const nodes = await getNodesFromSyntax(syntax)
      setTopSyntax(syntax)
      setTopNodes(nodes)
      setTopAxisError(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to parse syntax'
      setTopAxisError(message)
      throw error
    }
  }, [customer, job, getNodesFromSyntax, refreshTopSyntax])

  // Add checked variables from tree to top axis by building syntax string
  // We append variable names to the existing syntax and re-parse via tonodes,
  // rather than creating empty Codeframe nodes (which crash Carbon's fromnodes)
  const handleAddSelectedToTopAxis = useCallback(async (varNodes: GenNode[]) => {
    if (varNodes.length === 0) return

    const newVarNames = varNodes.map(n => n.value1 || '').filter(Boolean)
    if (newVarNames.length === 0) return

    const currentSyntax = topSyntax.trim()
    const addition = newVarNames.join(' + ')
    const newSyntax = currentSyntax ? `${currentSyntax} + ${addition}` : addition

    try {
      await handleTopSyntaxApply(newSyntax)
    } catch {
      // Error is already set via handleTopSyntaxApply
    }
  }, [topSyntax, handleTopSyntaxApply])

  // Recursive helper to update a node at any depth in the tree
  const updateNodeRecursively = (arr: GenNode[], targetId: number, updatedNode: GenNode): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === targetId)
    if (idx !== -1) {
      const copy = [...arr]
      copy[idx] = updatedNode
      return [copy, true]
    }

    let changed = false
    const next = arr.map(n => {
      if (n.children && n.children.length > 0) {
        const [childList, wasFound] = updateNodeRecursively(n.children, targetId, updatedNode)
        if (wasFound) {
          changed = true
          return { ...n, children: childList }
        }
      }
      return n
    })
    return [next, changed]
  }

  // Save filter — handles both global filter and node-level filter from AxisEditor
  const handleSaveFilter = useCallback((filterString: string) => {
    if (!filterTargetNode) {
      setFilter(filterString)
      return
    }

    // Edit existing Filter node
    if (filterTargetNode.type === 'Filter') {
      const updatedFilterNode: GenNode = {
        ...filterTargetNode,
        value1: filterString,
        value2: filterString,
      }
      const [newTop, topChanged] = updateNodeRecursively(topNodes, filterTargetNode.id!, updatedFilterNode)
      if (topChanged) {
        handleTopAxisChange(newTop)
      }
      setFilterTargetNode(null)
      return
    }

    // Wrap a Codeframe node with a Filter node
    const wrapNodeWithFilter = (nodes: GenNode[], targetId: number): [GenNode[], boolean] => {
      const idx = nodes.findIndex(n => n.id === targetId)
      if (idx !== -1) {
        const original = nodes[idx]
        const filterId = generateNodeId()
        const wrappedChild: GenNode = {
          ...original,
          parentId: filterId,
          level: (original.level || 0) + 1,
        }
        const filterNode: GenNode = {
          type: 'Filter',
          id: filterId,
          parentId: original.parentId,
          isExpanded: true,
          isFolder: true,
          value1: filterString,
          value2: filterString,
          level: original.level || 0,
          anyChildren: true,
          children: [wrappedChild],
        }
        const next = [...nodes]
        next.splice(idx, 1, filterNode)
        return [next, true]
      }
      let changed = false
      const next = nodes.map(n => {
        if (n.children && n.children.length > 0) {
          const [childList, did] = wrapNodeWithFilter(n.children, targetId)
          if (did) {
            changed = true
            return { ...n, children: childList }
          }
        }
        return n
      })
      return [next, changed]
    }

    const [newTop, topChanged] = wrapNodeWithFilter(topNodes, filterTargetNode.id!)
    if (topChanged) {
      handleTopAxisChange(newTop)
    }
    setFilterTargetNode(null)
  }, [filterTargetNode, topNodes, handleTopAxisChange])

  const handleOpenFilterModal = useCallback((node?: GenNode) => {
    setFilterTargetNode(node || null)
    setFilterModalOpen(true)
  }, [])

  const handleCloseFilterModal = useCallback(() => {
    setFilterModalOpen(false)
  }, [])

  const fetchVariableDescriptions = useCallback(async (varNames: string[]) => {
    const results: Record<string, string> = {}
    // Fetch in parallel with concurrency limit
    const batchSize = 10
    for (let i = 0; i < varNames.length; i += batchSize) {
      const batch = varNames.slice(i, i + batchSize)
      const promises = batch.map(async (name) => {
        try {
          const res = await axios.get<VariableMetadata>(
            `${API_BASE_URL}/job/${customer}/${job}/variables/${encodeURIComponent(name)}/metadata`
          )
          results[name] = res.data.description || ''
        } catch {
          results[name] = ''
        }
      })
      await Promise.all(promises)
    }
    setDescriptions(results)
    return results
  }, [customer, job])

  const buildTablePreviews = useCallback((selectedVarNames: string[], descs: Record<string, string>): BatchTablePreview[] => {
    return selectedVarNames.map(name => ({
      name: generateTableName(name),
      variable: name,
      description: descs[name] || '',
    }))
  }, [generateTableName])

  const submit = useCallback(async (selectedVarNames: string[], descs: Record<string, string>) => {
    setLoading(true)
    try {
      const tables = selectedVarNames.map(name => ({
        name: generateTableName(name),
        side: name,
        description: descs[name] || '',
      }))

      const res = await axios.post(`${API_BASE_URL}/tables/${customer}/${job}/batch-generate`, {
        tables,
        top: topSyntax,
        filter,
        weight,
        caseFilter,
        useFilter: !!filter,
        useWeight: !!weight,
        destination,
        execFolder: destinationFolder,
      })
      return res.data as { generated: Array<{ name: string; path: string }>; count: number }
    } finally {
      setLoading(false)
    }
  }, [customer, job, topSyntax, filter, weight, caseFilter, prefix, suffix, destination, destinationFolder, generateTableName])

  return {
    topSyntax, setTopSyntax,
    topNodes, topAxisError,
    handleTopAxisChange,
    handleTopSyntaxApply,
    handleAddSelectedToTopAxis,
    filter, setFilter,
    weight, setWeight,
    caseFilter, setCaseFilter,
    prefix, setPrefix,
    suffix, setSuffix,
    destinationFolder, setDestinationFolder,
    destination, setDestination,
    loading,
    descriptions,
    filterTargetNode,
    filterModalOpen,
    handleSaveFilter,
    handleOpenFilterModal,
    handleCloseFilterModal,
    generateTableName,
    fetchVariableDescriptions,
    buildTablePreviews,
    submit,
  }
}

export default useBatchGenerate
